import React, { useState, useEffect } from 'react';
import { Building2, User, FileText, Hash, Loader2, Trash2, AlertTriangle, X, Upload, Image as ImageIcon } from 'lucide-react';
import { apiService } from '../../services/api.service';
import {
  API_ENDPOINTS,
  AccountDeleteReasonLabels,
  FLEET_APP_EVENTS,
  emitFleetAppEvent
} from '../../config/environment';
import { logout } from '../../services/auth.service';
import { fleetToast } from '../../utils/toast';
import './ProfilePage.css';

const DELETE_REASONS = Object.entries(AccountDeleteReasonLabels).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ reasonType: null, reasonNote: '' });
  const [deleting, setDeleting] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  /* Logo veritabanında base64 gövde + MIME tipi olarak durur; data URL'i istemci kurar
     (courier-micro'da nesne deposu yok, ikili içerik bu desenle saklanıyor). */
  const logoDataUrl = profile?.logoBase64
    ? `data:${profile.logoContentType || 'image/png'};base64,${profile.logoBase64}`
    : null;

  const MAX_LOGO_BYTES = 256 * 1024;

  const handleLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';               // aynı dosya tekrar seçilebilsin
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      fleetToast.error('Logo yalnızca PNG, JPG veya WEBP olabilir.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      fleetToast.error(`Logo en fazla 256 KB olabilir (seçilen: ${Math.round(file.size / 1024)} KB).`);
      return;
    }

    setLogoBusy(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          resolve(result.slice(result.indexOf(',') + 1));   // data URL önekini at
        };
        reader.onerror = () => reject(new Error('read-error'));
        reader.readAsDataURL(file);
      });

      const res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.PROFILE_LOGO, {
        logoBase64: base64,
        contentType: file.type
      });

      if (res.success && res.data) {
        setProfile(res.data);
        fleetToast.success('Firma logosu güncellendi');
        // Sol menü/rozet aynı veriyi okuyor: tazelenmesi için haber ver.
        emitFleetAppEvent(FLEET_APP_EVENTS.FLEET_PROFILE_CHANGED);
      } else {
        fleetToast.error(res.data?.message || res.error || 'Logo yüklenemedi');
      }
    } catch {
      fleetToast.error('Logo yüklenirken hata oluştu');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoBusy(true);
    try {
      const res = await apiService.delete(API_ENDPOINTS.FLEET_PANEL.PROFILE_LOGO);
      if (res.success && res.data) {
        setProfile(res.data);
        fleetToast.success('Firma logosu kaldırıldı');
        emitFleetAppEvent(FLEET_APP_EVENTS.FLEET_PROFILE_CHANGED);
      } else {
        fleetToast.error(res.data?.message || res.error || 'Logo kaldırılamadı');
      }
    } catch {
      fleetToast.error('Logo kaldırılırken hata oluştu');
    } finally {
      setLogoBusy(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.PROFILE);
      if (response.success) {
        setProfile(response.data);
      } else {
        setError(response.error || 'Profil yüklenemedi');
      }
    } catch (err) {
      setError('Profil yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteForm.reasonType === null) {
      fleetToast.warn('Lütfen bir neden seçin');
      return;
    }
    setDeleting(true);
    try {
      const res = await apiService.delete(API_ENDPOINTS.AUTH.DELETE_ACCOUNT, {
        reasonType: deleteForm.reasonType,
        reasonNote: deleteForm.reasonNote || null,
      });
      if (res.success) {
        fleetToast.success('Hesabınız başarıyla silindi');
        setTimeout(() => logout(), 1500);
      } else {
        fleetToast.error(res.error || 'Hesap silinemedi');
      }
    } catch {
      fleetToast.error('Bir hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteForm({ reasonType: null, reasonNote: '' });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (!deleting) setShowDeleteModal(false);
  };

  useEffect(() => {
    if (!showDeleteModal) return;
    const handler = (e) => { if (e.key === 'Escape') closeDeleteModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showDeleteModal, deleting]);

  if (loading) {
    return (
      <div className="prof-page">
        <div className="page-loading">
          <Loader2 className="spinning" size={40} />
          <p>Profil bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="prof-page">
        <div className="page-empty">
          <p>{error || 'Profil bilgisi bulunamadı'}</p>
          <button onClick={loadProfile} className="btn btn-primary">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prof-page">
      <div className="page-header">
        <div className="header-left">
          <div className="page-icon"><Building2 size={20} /></div>
          <div className="page-title">
            <h1>Filo Profili</h1>
            <p>Filo bilgilerinizi görüntüleyin ve düzenleyin</p>
          </div>
        </div>
      </div>

      <div className="prof-content">
        <div className="prof-stats-grid">
          <div className="prof-stat-card">
            <div className="prof-stat-icon prof-icon-indigo"><Hash size={18} /></div>
            <div className="prof-stat-body">
              <span className="prof-stat-label">Hesap Numarası</span>
              <span className="prof-stat-value">{profile.accountNumber || '-'}</span>
            </div>
          </div>
          <div className="prof-stat-card">
            <div className="prof-stat-icon prof-icon-blue"><Building2 size={18} /></div>
            <div className="prof-stat-body">
              <span className="prof-stat-label">Onay Durumu</span>
              <span className={`prof-badge ${profile.isApproved ? 'prof-badge-green' : 'prof-badge-amber'}`}>
                {profile.isApproved ? 'Onaylandı' : 'Beklemede'}
              </span>
            </div>
          </div>
          <div className="prof-stat-card">
            <div className="prof-stat-icon prof-icon-emerald"><User size={18} /></div>
            <div className="prof-stat-body">
              <span className="prof-stat-label">Hesap Durumu</span>
              <span className={`prof-badge ${profile.isActive ? 'prof-badge-green' : 'prof-badge-red'}`}>
                {profile.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
          </div>
          <div className="prof-stat-card">
            <div className="prof-stat-icon prof-icon-cyan"><FileText size={18} /></div>
            <div className="prof-stat-body">
              <span className="prof-stat-label">Aktif Kurye</span>
              <span className="prof-stat-value">{profile.activeCouriers ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Firma logosu: panelde (sol menü + kullanıcı rozeti) filonun kendi markası görünür. */}
        <div className="prof-section">
          <h2 className="prof-section-title">Firma Logosu</h2>
          <div className="prof-logo-row">
            <div className="prof-logo-preview">
              {logoDataUrl
                ? <img src={logoDataUrl} alt="Firma logosu" />
                : <span className="prof-logo-empty"><ImageIcon size={22} /> Logo yok</span>}
            </div>
            <div className="prof-logo-actions">
              <p className="prof-logo-hint">
                PNG, JPG veya WEBP, en fazla 256 KB, kare (1:1) görsel en iyi görünür.
                Yüklediğin logo sol menüde ve kullanıcı rozetinde Hızlıyo görselinin yerine geçer.
              </p>
              <div className="prof-logo-buttons">
                <label className={`prof-logo-btn primary ${logoBusy ? 'disabled' : ''}`}>
                  <Upload size={14} />
                  {logoBusy ? 'Yükleniyor...' : (logoDataUrl ? 'Logoyu değiştir' : 'Logo yükle')}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoSelected}
                    disabled={logoBusy}
                    hidden
                  />
                </label>
                {logoDataUrl && (
                  <button
                    type="button"
                    className="prof-logo-btn ghost"
                    onClick={handleLogoRemove}
                    disabled={logoBusy}
                  >
                    <Trash2 size={14} /> Kaldır
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="prof-section">
          <h2 className="prof-section-title">Firma Bilgileri</h2>
          <div className="prof-info-grid">
              <div className="prof-info-item">
                <span className="prof-info-label">Filo Adı</span>
                <span className="prof-info-value">{profile.name || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">Yasal Ünvan</span>
                <span className="prof-info-value">{profile.legalName || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">Vergi Numarası</span>
                <span className="prof-info-value">{profile.taxNumber || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">Vergi Dairesi</span>
                <span className="prof-info-value">{profile.taxOffice || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">E-posta</span>
                <span className="prof-info-value">{profile.email || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">Telefon</span>
                <span className="prof-info-value">{profile.phone || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">İletişim Kişisi</span>
                <span className="prof-info-value">{profile.contactPersonName || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">İletişim Telefonu</span>
                <span className="prof-info-value">{profile.contactPersonPhone || '-'}</span>
              </div>
              <div className="prof-info-item prof-full-width">
                <span className="prof-info-label">Adres</span>
                <span className="prof-info-value">{profile.address || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">İl</span>
                <span className="prof-info-value">{profile.city || '-'}</span>
              </div>
              <div className="prof-info-item">
                <span className="prof-info-label">İlçe</span>
                <span className="prof-info-value">{profile.district || '-'}</span>
              </div>
            </div>
        </div>

        <div className="prof-section">
          <h2 className="prof-section-title">Hizmet Bölgeleri</h2>
          {profile.coverageAreaNames && profile.coverageAreaNames.length > 0 ? (
            <div className="prof-coverage-tags">
              {profile.coverageAreaNames.map((area, i) => (
                <span key={i} className="prof-coverage-tag">{area}</span>
              ))}
            </div>
          ) : (
            <p className="prof-empty-text">Henüz hizmet bölgesi tanımlanmamış</p>
          )}
        </div>

        <div className="prof-section prof-danger-section">
          <h2 className="prof-section-title prof-danger-title">
            <AlertTriangle size={18} />
            Tehlikeli Bölge
          </h2>
          <p className="prof-danger-desc">
            Hesabınızı sildiğinizde tüm verilerinize erişiminiz kalıcı olarak kapatılır. Bu işlem geri alınamaz.
          </p>
          <button className="prof-delete-btn" onClick={openDeleteModal}>
            <Trash2 size={16} />
            Hesabımı Sil
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="ms-modal prof-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ms-modal-header">
              <div className="ms-modal-title">
                <div className="ms-modal-icon prof-delete-modal-icon"><AlertTriangle size={20} /></div>
                <div>
                  <h2>Hesabı Sil</h2>
                  <span className="ms-modal-sub">Bu işlem geri alınamaz</span>
                </div>
              </div>
              <button className="ms-modal-close" onClick={closeDeleteModal}><X size={18} /></button>
            </div>
            <div className="ms-modal-body">
              <p className="prof-delete-warn">
                Hesabınızı silmek üzeresiniz. Silme işlemi sonrası hesabınız devre dışı bırakılır ve tekrar giriş yapamazsınız.
              </p>
              <div className="prof-delete-form">
                <label className="prof-delete-label">Hesabınızı neden silmek istiyorsunuz?</label>
                <div className="prof-delete-reasons">
                  {DELETE_REASONS.map((r) => (
                    <label key={r.value} className={`prof-reason-option ${deleteForm.reasonType === r.value ? 'prof-reason-selected' : ''}`}>
                      <input
                        type="radio"
                        name="deleteReason"
                        checked={deleteForm.reasonType === r.value}
                        onChange={() => setDeleteForm((f) => ({ ...f, reasonType: r.value }))}
                      />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
                <label className="prof-delete-label">Eklemek istediğiniz bir not var mı? (Opsiyonel)</label>
                <textarea
                  className="prof-delete-textarea"
                  rows={3}
                  maxLength={500}
                  placeholder="Deneyiminizi iyileştirmemize yardımcı olun..."
                  value={deleteForm.reasonNote}
                  onChange={(e) => setDeleteForm((f) => ({ ...f, reasonNote: e.target.value }))}
                />
              </div>
            </div>
            <div className="ms-modal-footer">
              <button className="ms-btn ms-btn-secondary" onClick={closeDeleteModal} disabled={deleting}>Vazgeç</button>
              <button
                className="ms-btn prof-btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteForm.reasonType === null}
              >
                {deleting ? <><Loader2 size={14} className="spinning" /> Siliniyor...</> : <><Trash2 size={14} /> Hesabı Sil</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
